import { useAnchorWallet } from "@solana/wallet-adapter-react";
import { getGovernance, Governance } from "@solana/spl-governance";
import { PublicKey } from "@solana/web3.js";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { EMPTY_STRING } from "../../common/constants/common.constants";
import { RPC_ERROR } from "../../common/constants/network.constants";

import { IClubDataSolana } from "../../common/interfaces/clubs.interface";
import {
  IBasicProposalData,
  IOffer,
} from "../../common/interfaces/proposals.interface";
import BuySellNft from "../../components/BuySellNft/BuySellNft";
import SkeletonItem from "../../components/SkeletonItem/SkeletonItem";
import {
  checkIfRpcWorks,
  getInfoForMintAccounts,
} from "../../solana/common/helpers/common.helpers";
import { RPC_CONNECTION } from "../../solana/common/utilities";
import {
  fetchBasicClubInfoFromSolana,
  getTreasuryForClub,
} from "../../solana/methods/clubs";
import { getProposalOffer } from "../../solana/methods/proposals";
import NotFound from "../NotFound/NotFound";
import "./BuySellNftPage.scss";
import { isMobile } from "react-device-detect";
import { getBasicProposalInfo } from "../../solana/common/helpers/proposal-types.helpers";
import { ProposalTypeForProgram } from "../../common/enums/proposals.enums";

const BuySellNftPage = () => {
  const [loading, toggleLoading] = useState(true);
  const params = useParams();
  const [offer, setOffer] = useState<IOffer>();
  const [club, setClub] = useState<IClubDataSolana>();
  const [error, setError] = useState(EMPTY_STRING);
  const [proposal, setProposal] = useState<IBasicProposalData>();
  const [tokenDecimals, setTokenDecimals] = useState();
  const [tokenSymbol, setTokenSymbol] = useState();

  const wallet = useAnchorWallet();
  const [proposalGovernance, setProposalGovernance] = useState<Governance>();

  useEffect(() => {
    void getProposalInfo();
  }, []);

  const getProposalInfo = async () => {
    if (!params || !params.proposalAddress || !params.clubAddress) {
      setError("Invalid offer address");
      return;
    }
    try {
      toggleLoading(true);
      const isRpcAvailable = await checkIfRpcWorks(setError);

      if (!isRpcAvailable) {
        return;
      }

      const club = await fetchBasicClubInfoFromSolana(params!.clubAddress!);

      if (!club) {
        setError("Club not found");
        return;
      }
      const clubsTreasury = await getTreasuryForClub(club?.title);
      setClub(club);
      const proposalData = await getBasicProposalInfo(
        new PublicKey(params.proposalAddress!)
      );
      const proposalGovernance = await getGovernance(
        RPC_CONNECTION,
        proposalData.proposalAccount.account.governance
      );
      const offer = await getProposalOffer(
        club.title,
        proposalData,
        proposalGovernance.account,
        wallet!,
        clubsTreasury.denominatedTokenInfo
      );

      const offerMintInfo = await getInfoForMintAccounts(
        proposalData.proposalMetadata.proposalType ===
          ProposalTypeForProgram.BuyP2P
          ? [offer.offeredTokenMint]
          : [offer.wantedTokenMint]
      );
      setTokenDecimals(offerMintInfo[0] && offerMintInfo[0].decimals);
      setTokenSymbol(offerMintInfo[0] && offerMintInfo[0].symbol);
      setProposalGovernance(proposalGovernance.account);
      setOffer(offer);
      setProposal(proposalData);
      setError(EMPTY_STRING);
    } catch (error) {
      setError("Error");
    } finally {
      toggleLoading(false);
    }
  };

  if (error) {
    return error.includes(RPC_ERROR) ? (
      <NotFound notFound={false} rpcError={error.includes(RPC_ERROR)} />
    ) : (
      <NotFound notFound={true} />
    );
  }

  if (loading) {
    return (
      <div className="skeleton-wrapper">
        <SkeletonItem
          width={isMobile ? 250 : 400}
          height={isMobile ? 30 : 50}
          borderRadius={40}
        />
        <SkeletonItem width={118} height={24} borderRadius={40} />
        <SkeletonItem width={100} height={100} borderRadius={10} />
        <SkeletonItem width={300} height={30} borderRadius={40} />
        <SkeletonItem
          width={isMobile ? 90 : 100}
          height={30}
          borderRadius={40}
        />
        <SkeletonItem width={180} height={50} borderRadius={40} />
      </div>
    );
  }
  return (
    <div className="buy-sell-nft-page">
      {proposal && proposalGovernance && tokenDecimals && tokenSymbol && (
        <BuySellNft
          offer={offer!}
          clubName={club?.title!}
          proposal={proposal}
          governance={proposalGovernance}
          decimals={tokenDecimals}
          symbol={tokenSymbol}
        />
      )}
    </div>
  );
};

export default BuySellNftPage;
